import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SvgIcon,
  Typography,
  useForkRef,
} from '@rossum/ui/material';
import { IconCustomMenuDown } from '../icons/tabler';
import { Prettify } from './typeUtils';

type SplitButtonActionBase = {
  label: string;
  icon?: React.ReactNode;
};

export type SplitButtonAction = SplitButtonActionBase &
  (
    | {
        callback: (
          e?: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>
        ) => void;
        formId?: string;
        'data-cy'?: string;
      }
    | {
        formId: string;
        callback?: (
          e?: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>
        ) => void;
        'data-cy'?: string;
      }
  );

export type SplitButtonProps = Prettify<
  ButtonGroupProps & {
    // at least one is required
    actions: [SplitButtonAction, ...SplitButtonAction[]];
    defaultLabel?: string;
    ActionButtonProps?: Prettify<
      Omit<ButtonProps, 'onClick'> & { 'data-cy': string }
    >;
    DropdownTriggerButtonProps?: Prettify<
      Omit<
        ButtonProps,
        'onClick' | 'aria-controls' | 'aria-expanded' | 'aria-haspopup'
      > & { 'data-cy'?: string }
    >;
    defaultActionIndex?: number;
  }
>;

export const SplitButton: ForwardRefExoticComponent<
  SplitButtonProps & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      actions,
      defaultActionIndex = 0,
      defaultLabel,
      ActionButtonProps,
      DropdownTriggerButtonProps,
      ...buttonGroupProps
    },
    forwardedRef
  ) => {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const handleAnchorRef = useForkRef(forwardedRef, setAnchorEl);

    useEffect(() => {
      if (defaultActionIndex >= actions.length) {
        throw new Error(
          '[SplitButton]: `defaultActionIndex` is out of bounds of `actions`'
        );
      }
    }, [actions.length, defaultActionIndex]);

    const handleToggle = () => setOpen(o => !o);

    // actions is a non-empty tuple, so actions[0] is always defined.
    // defaultActionIndex is validated in useEffect above, so this is safe.
    const defaultAction = actions[defaultActionIndex] ?? actions[0];

    const handleClose = (event: MouseEvent | TouchEvent) => {
      if (triggerRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    return (
      <>
        <ButtonGroup
          ref={handleAnchorRef}
          disableElevation
          {...buttonGroupProps}
          sx={{ alignSelf: 'center', ...buttonGroupProps.sx }}
        >
          <Button
            {...(defaultAction.formId
              ? {
                  type: 'submit' as const,
                  form: defaultAction.formId,
                }
              : {})}
            {...(defaultAction.callback
              ? { onClick: defaultAction.callback }
              : {})}
            data-cy={defaultAction['data-cy']}
            {...ActionButtonProps}
          >
            {defaultLabel ?? defaultAction.label}
          </Button>
          <Button
            ref={triggerRef}
            onClick={handleToggle}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select action"
            aria-haspopup="menu"
            {...DropdownTriggerButtonProps}
            sx={{
              ...DropdownTriggerButtonProps?.sx,
              '&&': {
                minWidth: 'unset',
                aspectRatio: '1 / 1',
                padding: 1,
              },
            }}
            size="small"
          >
            <SvgIcon
              sx={{
                transition: 'transform 0.2s',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <IconCustomMenuDown />
            </SvgIcon>
          </Button>
        </ButtonGroup>
        <Popper
          open={open}
          anchorEl={anchorEl}
          role={undefined}
          placement="bottom-end"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ||
                  placement === 'bottom-end' ||
                  placement === 'bottom-start'
                    ? 'center top'
                    : 'center bottom',
                width: '100%',
              }}
            >
              <Paper elevation={6}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    {actions
                      .filter((_, index) => index !== defaultActionIndex)
                      .map(action => (
                        <MenuItem
                          key={action.label}
                          onClick={e => {
                            if (action.formId) {
                              const form = document.getElementById(
                                action.formId
                              ) as HTMLFormElement | null;
                              form?.requestSubmit();
                            }
                            action.callback?.(e);
                            setOpen(false);
                          }}
                          data-cy={action['data-cy']}
                          disabled={buttonGroupProps.disabled}
                          sx={{ gap: 1 }}
                        >
                          {action.icon}
                          <Typography variant="body2">
                            {action.label}
                          </Typography>
                        </MenuItem>
                      ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }
);

SplitButton.displayName = 'SplitButton';
